/**
 * LocalPhotosPanel — left-side photos panel for the manual "Add New Product"
 * flow. Mirrors the URL-based PhotosPanel (in ImportFromUrlModal) but the
 * source is local File uploads instead of pre-extracted image URLs.
 *
 * Parent state contract (kept compatible with InventoryProductForm validator):
 *   product._local_files       Array<{ id, file, previewUrl }>
 *   product._selected_images   Array<string>   // mirror of _local_files ids
 *   product._tryon_image_url   string | null   // id of the chosen tryon
 *   product.product_name       (used for hero alt text)
 */
import React, { useEffect, useRef, useState } from 'react';
import { Star, Check, Upload, Trash2, ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { MAX_UPLOAD_MB, MAX_UPLOAD_BYTES } from '../../config/uploadLimits';

const MAX_FILES = 5;

const LocalPhotosPanel = ({ product, onUpdate, validationError }) => {
  const files = product._local_files || [];
  const tryonId = product._tryon_image_url || (files.length === 1 ? files[0]?.id : null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  // Cleanup blob URLs on unmount.
  useEffect(() => () => {
    files.forEach(f => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    // Intentionally only on unmount — re-running on every files change would
    // revoke the URL of currently-rendered images.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ingestFiles = (rawFiles) => {
    const incoming = Array.from(rawFiles || []).filter(f => f.type.startsWith('image/'));
    if (rawFiles?.length && incoming.length === 0) {
      toast.error('Only image files are allowed');
      return;
    }
    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} photos per product`);
      return;
    }
    const accepted = [];
    for (const f of incoming.slice(0, remaining)) {
      if (f.size > MAX_UPLOAD_BYTES) {
        toast.error(`${f.name} is too large (max ${MAX_UPLOAD_MB}MB)`);
        continue;
      }
      accepted.push({
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
      });
    }
    if (accepted.length === 0) return;
    if (incoming.length > remaining) {
      toast(`Only added ${remaining} of ${incoming.length} photos (max ${MAX_FILES})`, { icon: 'ℹ️' });
    }
    const nextFiles = [...files, ...accepted];
    const nextSelected = nextFiles.map(f => f.id);
    const nextTryon = tryonId || (nextFiles.length === 1 ? nextFiles[0].id : null);
    onUpdate({
      _local_files: nextFiles,
      _selected_images: nextSelected,
      _tryon_image_url: nextTryon,
    });
  };

  const removeFile = (id) => {
    const target = files.find(f => f.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    const nextFiles = files.filter(f => f.id !== id);
    const nextSelected = nextFiles.map(f => f.id);
    let nextTryon = tryonId === id ? null : tryonId;
    if (!nextTryon && nextFiles.length === 1) nextTryon = nextFiles[0].id;
    onUpdate({
      _local_files: nextFiles,
      _selected_images: nextSelected,
      _tryon_image_url: nextTryon,
    });
  };

  const setTryon = (id) => onUpdate({ _tryon_image_url: id });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    ingestFiles(e.dataTransfer?.files);
  };
  const handleInput = (e) => {
    ingestFiles(e.target.files);
    e.target.value = '';
  };

  const heroFile = files.find(f => f.id === tryonId) || files[0];

  return (
    <div className="bg-slate-50 border-r border-slate-200 flex flex-col h-full min-h-0" data-testid="local-photos-panel">
      {/* Hero preview */}
      <div className="aspect-[3/4] max-h-[40vh] bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
        {heroFile ? (
          <img src={heroFile.previewUrl} alt={product.product_name || 'product photo'} className="w-full h-full object-contain" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-slate-300">
            <ImageIcon className="w-10 h-10" />
            <span className="text-xs">No photos yet</span>
          </div>
        )}
      </div>

      {/* Upload + thumbnails */}
      <div
        className="p-3 flex-1 overflow-y-auto min-h-0"
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-medium text-slate-600">
            Photos <span className="text-slate-400">({files.length}/{MAX_FILES})</span>
          </p>
          {files.length > 1 && (
            <p className="text-[10px] text-slate-500">
              <Star className="w-3 h-3 inline -mt-0.5" /> = Try-on
            </p>
          )}
        </div>

        {/* Upload dropzone */}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={files.length >= MAX_FILES}
          className={`w-full mb-2.5 px-3 py-3 rounded-lg border-2 border-dashed text-xs font-medium transition-all ${
            dragActive
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : files.length >= MAX_FILES
              ? 'border-slate-200 text-slate-400 cursor-not-allowed bg-white'
              : 'border-slate-300 text-slate-600 hover:border-blue-400 hover:bg-blue-50/40 bg-white'
          }`}
          data-testid="local-photos-dropzone"
        >
          <Upload className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {files.length >= MAX_FILES
            ? 'Maximum reached'
            : 'Drop photos here or click to upload'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleInput}
          className="hidden"
          data-testid="local-photos-input"
        />

        {/* Thumbnails */}
        {files.length > 0 && (
          <div className="grid grid-cols-3 gap-1.5">
            {files.map((f) => {
              const isTry = tryonId === f.id;
              return (
                <div key={f.id} className="relative group">
                  <div
                    className={`relative w-full aspect-square overflow-hidden rounded-md border-2 transition-all ${
                      isTry ? 'border-amber-400 ring-1 ring-amber-200' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`local-photo-thumb-${f.id}`}
                  >
                    <img src={f.previewUrl} alt="" className="w-full h-full object-cover" />
                    {isTry && (
                      <div className="absolute top-0.5 right-0.5 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </div>
                  {/* Try-on toggle */}
                  {files.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setTryon(f.id)}
                      className={`absolute bottom-0.5 left-0.5 p-1 rounded-full transition-all ${
                        isTry
                          ? 'bg-amber-400 text-white'
                          : 'bg-white/90 text-slate-400 hover:text-amber-500 opacity-0 group-hover:opacity-100'
                      }`}
                      title="Set as Try-On image"
                      data-testid={`local-photo-tryon-${f.id}`}
                    >
                      <Star className="w-3 h-3" fill={isTry ? 'currentColor' : 'none'} />
                    </button>
                  )}
                  {/* Remove */}
                  <button
                    type="button"
                    onClick={() => removeFile(f.id)}
                    className="absolute bottom-0.5 right-0.5 p-1 bg-white/90 text-slate-400 hover:text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                    title="Remove photo"
                    data-testid={`local-photo-remove-${f.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {validationError && <p className="text-[11px] text-red-500 mt-2">{validationError}</p>}
        {files.length === 0 && (
          <p className="text-[11px] text-slate-400 italic mt-2">
            Add at least one photo. The first one becomes the Try-On input by default.
          </p>
        )}
      </div>
    </div>
  );
};

export default LocalPhotosPanel;
